import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Shield, Phone, Menu, X, MessageSquare } from "lucide-react";
import api from "@/lib/api";

export const useContent = () => {
  const [content, setContent] = useState(null);
  useEffect(() => { api.get("/content").then((r) => setContent(r.data)).catch(() => {}); }, []);
  return content;
};

const NAV = [
  { to: "/auto-insurance", label: "Auto" },
  { to: "/home-insurance", label: "Home" },
  { to: "/auto-home-bundle", label: "Bundle" },
  { to: "/commercial-insurance", label: "Business" },
  { to: "/life-insurance", label: "Life" },
  { to: "/refer", label: "Refer a Friend" },
];

export default function PublicLayout({ children }) {
  const content = useContent();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); setOpen(false); }, [pathname]);
  const phone = content?.phone || "(570) 661-3771";

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#0B1E36] flex flex-col">
      {content?.announcement && (
        <div className="bg-[#0B1E36] text-white text-center text-xs sm:text-sm py-2 px-4" data-testid="announcement-bar">
          {content.announcement}
        </div>
      )}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <Shield className="text-[#C5A059]" strokeWidth={2} />
            <span className="font-display text-lg font-bold tracking-tight">Keystone Shield<span className="text-[#C5A059]">.</span></span>
          </Link>
          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} className="text-sm font-medium hover:text-[#C5A059] transition-colors" data-testid={`nav-${n.label.toLowerCase().replace(/\s/g,'-')}`}>{n.label}</Link>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <a href={`tel:${phone}`} className="hidden sm:flex items-center gap-2 text-sm font-semibold" data-testid="header-call"><Phone size={16} /> {phone}</a>
            <a href={`sms:${content?.text_number || '5706613771'}`} className="hidden md:flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-[#C5A059]" data-testid="header-text"><MessageSquare size={15} /> Text Us</a>
            <Link to="/quote" className="hidden sm:inline-block bg-[#C5A059] text-[#0B1E36] font-bold text-sm px-4 py-2.5 rounded-sm hover:bg-[#0B1E36] hover:text-white transition-colors" data-testid="header-get-quote">GET A FREE QUOTE</Link>
            <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">{open ? <X /> : <Menu />}</button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden border-t border-slate-200 bg-white px-5 py-4 space-y-3">
            {NAV.map((n) => <Link key={n.to} to={n.to} className="block text-sm font-medium py-1" data-testid={`mnav-${n.label.toLowerCase().replace(/\s/g,'-')}`}>{n.label}</Link>)}
            <Link to="/crm/login" className="block text-sm text-slate-500 py-1" data-testid="mnav-login">Employee Login</Link>
          </div>
        )}
      </header>

      <main className="flex-1 pb-24 lg:pb-0">{children}</main>

      <footer className="bg-[#0B1E36] text-white mt-auto">
        <div className="max-w-7xl mx-auto px-5 py-14 grid md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3"><Shield className="text-[#C5A059]" /><span className="font-display font-bold">Keystone Shield</span></div>
            <p className="text-sm text-slate-300">An independent insurance agency licensed in PA, NJ, NY & FL. Fast, free, no-obligation quotes.</p>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-bold mb-3">Coverage</h4>
            <ul className="space-y-2 text-sm text-slate-300">
              {NAV.slice(0,5).map(n => <li key={n.to}><Link to={n.to} className="hover:text-white">{n.label} Insurance</Link></li>)}
            </ul>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-bold mb-3">Agency</h4>
            <ul className="space-y-2 text-sm text-slate-300">
              <li>{content?.address}</li>
              <li>{content?.hours}</li>
              <li><a href={`tel:${phone}`} className="hover:text-white">{phone}</a></li>
              <li><Link to="/reviews" className="hover:text-white">Leave a Review</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-bold mb-3">Legal</h4>
            <ul className="space-y-2 text-sm text-slate-300">
              <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-white">Terms of Use</Link></li>
              <li><Link to="/crm/login" className="hover:text-white">Employee Login</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs text-slate-400 px-5">
          © {new Date().getFullYear()} Keystone Shield Insurance. Coverage, eligibility, and rates are determined by the insurance carrier and are not guaranteed.
        </div>
      </footer>

      {/* sticky mobile bar */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 grid grid-cols-2 border-t border-slate-200">
        <a href={`tel:${phone}`} className="bg-[#0B1E36] text-white font-bold py-4 flex items-center justify-center gap-2" data-testid="sticky-call"><Phone size={18} /> CALL NOW</a>
        <Link to="/quote" className="bg-[#C5A059] text-[#0B1E36] font-bold py-4 flex items-center justify-center" data-testid="sticky-quote">GET A QUOTE</Link>
      </div>
    </div>
  );
}
